/**
 * Main App Component
 * Sets up routing for the application
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardHybrid from './pages/DashboardHybrid';
import DashboardGlobal from './pages/DashboardGlobal';
import DashboardTech from './pages/DashboardTech';
import AccountPage from './components/account/AccountPage';
import Landing from './components/landing/Landing';
import SignIn from './components/signin/SignIn';
import SignUp from './components/signup/SignUp';
import Subscriptions from './components/subscriptions/Subscriptions';
import Investors from './components/investors/Investors';
import './App.css';

// Component to handle default route based on authentication status
function DefaultRoute() {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#212529',
                color: '#f0f0f0'
            }}>
                Loading...
            </div>
        );
    }
    
    // If authenticated, redirect to account page; otherwise show landing page
    return isAuthenticated ? <Navigate to="/account" replace /> : <Landing />;
}

function App() {
    return (
        <AuthProvider>
            <Router>
            <div className="app">
                <Routes>
                    {/* Landing Page Route - Marketing/landing page */}
                    <Route 
                        path="/landing" 
                        element={<Landing />} 
                    />
                    
                    {/* Dashboard Routes - Different dashboard types (PROTECTED) */}
                    <Route 
                        path="/dashboard" 
                        element={
                            <ProtectedRoute>
                                <DashboardHybrid />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/dashboard/global-markets" 
                        element={
                            <ProtectedRoute>
                                <DashboardGlobal />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/dashboard/global-technology" 
                        element={
                            <ProtectedRoute>
                                <DashboardTech />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/dashboard/hybrid" 
                        element={
                            <ProtectedRoute>
                                <DashboardHybrid />
                            </ProtectedRoute>
                        } 
                    />
                    
                    {/* Account Route - User account management (PROTECTED) */}
                    <Route 
                        path="/account" 
                        element={
                            <ProtectedRoute>
                                <AccountPage />
                            </ProtectedRoute>
                        } 
                    />
                    
                    {/* Sign In Route - User authentication */}
                    <Route 
                        path="/signin" 
                        element={<SignIn />} 
                    />
                    
                    {/* Sign Up Route - User registration */}
                    <Route 
                        path="/signup" 
                        element={<SignUp />} 
                    />
                    
                    {/* Subscriptions Route - Pricing and plans */}
                    <Route 
                        path="/subscriptions" 
                        element={<Subscriptions />} 
                    />
                    
                    {/* Investors Route - Institutional investors contact page */}
                    <Route 
                        path="/investors" 
                        element={<Investors />} 
                    />
                    
                    {/* Default route - Redirect to account if authenticated, otherwise show landing */}
                    <Route 
                        path="/" 
                        element={<DefaultRoute />} 
                    />
                    
                    {/* Catch-all route - Redirect unknown paths to landing */}
                    <Route 
                        path="*" 
                        element={<Navigate to="/landing" replace />} 
                    />
                </Routes>
            </div>
        </Router>
        </AuthProvider>
    );
}

export default App;
